import { useState, useEffect } from 'react'
import { useModal } from 'components/modals/provider'
import InputImage from 'components/input-image/input-image'
import Input from 'components/input/input/input'
import Checkbox from 'components/input/checkbox/checkbox'
import Textarea from 'components/input/textarea/textarea'
import Select from 'components/input/select/select'
import Radio from 'components/input/radio/radio'
import InputGroup from 'components/input-group/input-group'
import Tabs from 'components/tabs/tabs'
import TabContent from 'components/tabs/content'
import Infoblock from 'components/infoblock/infoblock'
import Btn from 'components/btn/btn'
import BtnGroup from 'components/btn-group/btn-group'
import Menu from 'components/menu/menu'
import Privacy from 'components/privacy/privacy'
import Heading from 'components/heading/heading'
import ProductFeatures from 'components/product-features/product-features'
import A from 'components/a/a'
import DeliverySettings from 'components/delivery-settings/delivery-settings'
import formStyles from 'components/form/form.module.scss'
import styles from './account-settings.module.scss'
import { cn, getRoute, parseFormEvent } from 'utils/helpers'
import { useGet, getUrl } from 'utils/api'
import certificateTypeOptions from 'utils/data/certificateTypes'

const discountOptions = [
    {
        name: '0%',
        value: 0
    },
    {
        name: '5%',
        value: 5
    },
    {
        name: '10%',
        value: 10
    },
    {
        name: '15%',
        value: 15
    },
    {
        name: '20%',
        value: 20
    },
    {
        name: '25%',
        value: 25
    },
    {
        name: '30%',
        value: 30
    },
    {
        name: '35%',
        value: 35
    },
    {
        name: '40%',
        value: 40
    },
    {
        name: '45%',
        value: 45
    },
    {
        name: '50%',
        value: 50
    }
]

export default function AccountSettings({ endpoint, forRegistration, isAdmin, onSubmit }) {
    const [tab, setTab] = useState(0)
    const [account, setAccount] = useState()
    const { showConfirm } = useModal()

    const { data } = useGet(endpoint)

    useEffect(async () => {
        if (data) {
            if (!data.deliveries) {
                data.deliveries = { courier: 1, omniva: 1, dpd: 1, lpexpress: 1, venipak: 1, itella: 1, post_office: 1 }
                data.delivery_prices = {
                    courier: '1.99',
                    omniva: '1.99',
                    dpd: '1.99',
                    lpexpress: '1.99',
                    venipak: '1.99',
                    itella: '1.99',
                    post_office: '1.99'
                }
            }

            setAccount(data)
        }
    }, [data])

    const handleChange = e => {
        const { name, value } = parseFormEvent(e)
        setAccount({ ...account, [name]: value })
    }

    if (!account) return null

    const privacyPolicyTypes = [
        { value: 'generated', name: 'Naudoti sugeneruotą politiką' },
        { value: 'custom', name: 'Naudoti savo' }
    ]

    const menuItems = [
        {
            text: 'Pagrindinė informacija',
            no: '1',
            onClick: tab > 0 ? () => setTab(0) : null
        },
        {
            text: 'Prekių savybės',
            no: '2',
            onClick: tab > 1 ? () => setTab(1) : null
        },
        {
            text: 'Siuntimas',
            no: '3',
            onClick: tab > 2 ? () => setTab(2) : null
        },
        {
            text: 'Mokėjimai',
            no: '4',
            onClick: tab > 3 ? () => setTab(3) : null
        },
        {
            text: 'Privatumo politika',
            no: '5',
            onClick: tab > 4 ? () => setTab(4) : null
        }
    ]

    const handleNextStep = async e => {
        if (forRegistration) {
            e.preventDefault()

            const inputs = e.target.querySelectorAll('input[required]')
            let valid = true
            inputs.forEach(input => {
                const visible = input.offsetWidth > 0 && input.offsetHeight > 0
                if (visible && !input.checkValidity()) {
                    valid = false
                }
            })

            if (valid) {
                e.preventDefault()
                if (tab == 0) {
                    setTab(1)
                } else if (tab == 1) {
                    setTab(2)
                } else if (tab == 2) {
                    setTab(3)
                } else {
                    showConfirm('Ar patvirtinate, kad visa pateikta informacija teisinga?').then(confirmed => {
                        if (confirmed) {
                            onSubmit(account)
                        }
                    })
                }
            }
        } else {
            e.preventDefault()
            onSubmit(account)
        }
    }

    return (
        <form onSubmit={handleNextStep} className={cn(forRegistration && styles.registration)}>
            {forRegistration ? (
                <div className={styles.left}>
                    <Menu items={menuItems} activeUntil={tab} />
                </div>
            ) : (
                <Tabs
                    labels={[
                        'Pagrindinė informacija',
                        'Prekių savybės',
                        'Siuntimas',
                        'Mokėjimai',
                        'Privatumo politika'
                    ]}
                    active={tab}
                    setActive={setTab}
                    border
                    className="mb36"
                />
            )}
            <TabContent active={tab} panel={forRegistration}>
                <div>
                    <div className={formStyles.form}>
                        <div className={formStyles.left}>
                            <InputImage
                                model="Account"
                                value={account.image}
                                name="image"
                                onChange={handleChange}
                                label="Įkelkite logotipą (neprivaloma)"
                                logo
                            />
                            <InputImage
                                model="Account"
                                value={account.cover}
                                name="cover"
                                onChange={handleChange}
                                label="Fono paveikslėlis (neprivaloma)"
                            />
                            <InputImage
                                model="Account"
                                value={account.avatar}
                                name="avatar"
                                onChange={handleChange}
                                label="Kontaktinio asmens nuotrauka (neprivaloma)"
                            />
                        </div>
                        <div className={formStyles.right}>
                            {!!isAdmin && (
                                <>
                                    <Checkbox
                                        name="verified"
                                        value={account.verified}
                                        onChange={handleChange}
                                        label="Patvirtintas pardavėjas"
                                    />
                                    <Checkbox
                                        name="custom_contract"
                                        value={account.custom_contract}
                                        onChange={handleChange}
                                        label="Atskiras sutarties numeris"
                                    />
                                </>
                            )}
                            <InputGroup>
                                <Input
                                    type="text"
                                    name="name"
                                    value={account.name}
                                    onChange={handleChange}
                                    label="Parduotuvės pavadinimas"
                                    required
                                />
                                {!forRegistration && account.simplified && (
                                    <Input
                                        type="text"
                                        name="brand_name"
                                        value={account.brand_name}
                                        onChange={handleChange}
                                        label="Prekės ženklo pavadinimas"
                                        required
                                    />
                                )}
                            </InputGroup>
                            <InputGroup>
                                <Input
                                    type="email"
                                    name="email"
                                    value={account.email}
                                    onChange={handleChange}
                                    label="El. pašto adresas"
                                    required
                                />
                                <Input
                                    type="text"
                                    name="phone"
                                    value={account.phone}
                                    onChange={handleChange}
                                    label="Telefono numeris"
                                    required={forRegistration}
                                />
                            </InputGroup>
                            <Input
                                type="text"
                                name="contact_person"
                                value={account.contact_person}
                                onChange={handleChange}
                                label="Kontaktinis asmuo"
                            />
                            <Input
                                type="text"
                                name="short_description"
                                value={account.short_description}
                                onChange={handleChange}
                                label="Trumpas aprašymas"
                                required={forRegistration}
                                maxLength="60"
                                info="Apibūdinkite savo parduotuvę vienu trumpu sakiniu."
                            />
                            <Textarea
                                name="description"
                                value={account.description}
                                onChange={handleChange}
                                label="Aprašymas"
                                maxLength="4000"
                            />
                            <InputGroup>
                                <Checkbox
                                    name="ads_enabled"
                                    value={account.ads_enabled}
                                    onChange={handleChange}
                                    label="Įjungti išorinę reklamą."
                                    moreLink="https://zinynas.lietuviskapreke.lt/lesu-ismokejimas-ir-mokesciai#kada-taikomas-isorin-s-reklamos-mokestis"
                                />
                                {!!account.ads_enabled && (
                                    <Select
                                        name="discount_size"
                                        value={account.discount_size}
                                        onChange={handleChange}
                                        label="Maksimalus nuolaidos dydis"
                                        options={discountOptions}
                                        infoUrl="https://zinynas.lietuviskapreke.lt/nuolaidu-kodai"
                                        info="Išorinėje reklamoje naudojame nuolaidos kodus. Juos panaudojus pirkėjui paieškoje aukščiau matomos prekės su nuolaidomis. Spauskite čia, jeigu norite sužinoti daugiau."
                                    />
                                )}
                            </InputGroup>

                            {!!forRegistration || (
                                <>
                                    <Checkbox
                                        name="do_not_change"
                                        value={account.do_not_change}
                                        onChange={handleChange}
                                        label="Neleisti administratoriaus redagavimo."
                                        moreLink="https://zinynas.lietuviskapreke.lt/prekiu-kurimas/reikalavimai-ir-patvirtinimas#administratoriaus-redagavimas"
                                    />
                                    <Checkbox
                                        name="temp_unavailable"
                                        value={account.temp_unavailable}
                                        onChange={handleChange}
                                        label="Laikinai stabdyti prekybą"
                                        info="Pažymėjus šį laukelį prekės nebus galima įsigyti, tačiau el. parduotuvėje ji bus matoma."
                                    />
                                    <Checkbox
                                        name="advanced_product_settings"
                                        value={account.advanced_product_settings}
                                        onChange={handleChange}
                                        label="Naudoti papildomus prekės nustatymus"
                                        info="Naudoti galimybę prekėms kiekvienai atskirai nurodyti laiką iki išsiuntimo, galimus siuntimo būdus ir kita."
                                    />
                                </>
                            )}
                            {!!forRegistration && (
                                <Infoblock>
                                    Šį pasirinkimą galėsite keisti bet kuriuo metu. Daugiau informacijos apie išorinę
                                    reklamą ir mokesčius galite rasti{' '}
                                    <A href={getUrl('contract-example')} download>
                                        tarpininkavimo sutarties pavyzdyje
                                    </A>
                                    .
                                </Infoblock>
                            )}
                        </div>
                    </div>
                    {forRegistration && (
                        <div className="mt36">
                            <Textarea
                                name="discover"
                                value={account.discover}
                                onChange={handleChange}
                                label="Iš kur sužinojote apie lietuviskapreke.lt?"
                                maxLength="1000"
                            />
                            <BtnGroup withBorder>
                                <Btn style="yellow">Kitas žingsnis</Btn>
                            </BtnGroup>
                        </div>
                    )}
                </div>
                <div>
                    <ProductFeatures item={account} onChange={handleChange} />
                    {forRegistration && (
                        <BtnGroup withBorder>
                            <Btn style="yellow">Kitas žingsnis</Btn>
                        </BtnGroup>
                    )}
                </div>
                <div>
                    <DeliverySettings item={account} forAccount handleChange={handleChange} />
                    {forRegistration && (
                        <BtnGroup withBorder>
                            <Btn style="yellow">Kitas žingsnis</Btn>
                        </BtnGroup>
                    )}
                </div>
                <div>
                    <Checkbox
                        name="is_company"
                        value={account.is_company}
                        onChange={handleChange}
                        label="Pardavėjas yra įmonė"
                    />
                    <Checkbox name="has_vat" value={account.has_vat} onChange={handleChange} label="PVM mokėtojas" />
                    {account.is_company ? (
                        <>
                            <InputGroup>
                                <Input
                                    type="text"
                                    name="company_name"
                                    value={account.company_name}
                                    onChange={handleChange}
                                    label="Įmonės pavadinimas"
                                    required
                                />
                                <Input
                                    type="text"
                                    name="company_code"
                                    value={account.company_code}
                                    onChange={handleChange}
                                    label="Įmonės kodas"
                                    required
                                />
                            </InputGroup>
                            <InputGroup>
                                <Input
                                    type="text"
                                    name="company_address"
                                    value={account.company_address}
                                    onChange={handleChange}
                                    label="Įmonės adresas"
                                    required
                                />
                                {forRegistration && (
                                    <Input
                                        type="text"
                                        name="company_director"
                                        value={account.company_director}
                                        onChange={handleChange}
                                        label="Įmonės vadovas"
                                        required
                                    />
                                )}
                            </InputGroup>
                        </>
                    ) : (
                        <>
                            <InputGroup>
                                <Input
                                    type="text"
                                    name="person_first_name"
                                    value={account.person_first_name}
                                    onChange={handleChange}
                                    label="Vardas"
                                    required
                                />
                                <Input
                                    type="text"
                                    name="person_last_name"
                                    value={account.person_last_name}
                                    onChange={handleChange}
                                    label="Pavardė"
                                    required
                                />
                                {forRegistration && (
                                    <Input
                                        type="text"
                                        name="person_address"
                                        value={account.person_address}
                                        onChange={handleChange}
                                        label="Adresas"
                                        info="Sutarties sudarymui"
                                        required
                                    />
                                )}
                            </InputGroup>
                            <InputGroup>
                                <Select
                                    name="person_certificate_type"
                                    value={account.person_certificate_type}
                                    onChange={handleChange}
                                    label="Veiklos pagrindas"
                                    options={certificateTypeOptions}
                                    required
                                />
                                {account.person_certificate_type == 'license' ? (
                                    <Input
                                        type="text"
                                        name="person_personal_code"
                                        value={account.person_personal_code}
                                        onChange={handleChange}
                                        label="Asmens kodas"
                                        required
                                        info="Sutarčiai sudaryti ir sąskaitoms"
                                    />
                                ) : (
                                    <Input
                                        type="text"
                                        name="person_certificate_nr"
                                        value={account.person_certificate_nr}
                                        onChange={handleChange}
                                        label="Pažymos numeris"
                                        required
                                    />
                                )}
                            </InputGroup>
                        </>
                    )}
                    {!!account.has_vat && (
                        <InputGroup>
                            <Input
                                type="text"
                                name="company_vat"
                                value={account.company_vat}
                                onChange={handleChange}
                                label="PVM kodas"
                                required
                            />
                            <Input
                                type="number"
                                name="company_vat_size"
                                value={account.company_vat_size}
                                onChange={handleChange}
                                label="PVM dydis"
                                required
                            />
                        </InputGroup>
                    )}
                    {forRegistration && (
                        <Input
                            type="text"
                            name="bank_account_number"
                            value={account.bank_account_number}
                            onChange={handleChange}
                            label="Banko sąskaitos numeris"
                            required
                            info="Į šią banko sąskaitą bus pervedamos lėšos už jūsų prekes"
                        />
                    )}
                    {forRegistration && (
                        <BtnGroup withBorder>
                            <Btn style="yellow">Kitas žingsnis</Btn>
                        </BtnGroup>
                    )}
                </div>
                <div>
                    <Heading size="18" tag="h2" className="mb30">
                        Jūsų privatumo politika, kurią taikote savo pirkėjų duomenims
                    </Heading>
                    <Radio
                        name="privacy_policy_type"
                        value={account.privacy_policy_type}
                        onChange={handleChange}
                        options={privacyPolicyTypes}
                        required
                    />
                    {account.privacy_policy_type === 'generated' && (
                        <div className={styles.generatedPrivacy}>
                            <Privacy account={account} />
                        </div>
                    )}
                    {account.privacy_policy_type === 'custom' && (
                        <Textarea
                            name="privacy_policy"
                            value={account.privacy_policy}
                            onChange={handleChange}
                            label="Privatumo politika"
                            required
                        />
                    )}
                    {forRegistration && (
                        <BtnGroup withBorder>
                            <Btn style="yellow">Registruotis</Btn>
                        </BtnGroup>
                    )}
                </div>
            </TabContent>
            {!forRegistration && (
                <BtnGroup withBorder>
                    <Btn style="light" external href={getRoute('shop', account)}>
                        Peržiūrėti
                    </Btn>
                    <Btn style="yellow">Išsaugoti</Btn>
                </BtnGroup>
            )}
        </form>
    )
}
